import React, { HTMLAttributes } from 'react';
import { NameAvatar } from './NameAvatar';
import { UnidentifiedAvatar } from './UnidentifiedAvatar';
import { ImageAvatar } from './ImageAvatar';
import { IconAvatar } from './IconAvatar';

export type NameType = {
  firstName: string;
  lastName: string | null;
};

export interface AvatarProps extends HTMLAttributes<HTMLDivElement> {
  variant: 'name' | 'unidentified' | 'image' | 'icon';
  children?: React.ReactNode;
  size?: 'small' | 'default' | 'large' | 'extra-large';
  name?: NameType;
  imageUrl?: string;
  backgroundColor?: string;
  staticBackground?: boolean;
}

export const Avatar = ({
  variant,
  children,
  className,
  size,
  name,
  imageUrl,
  backgroundColor,
  staticBackground,
  ...rest
}: AvatarProps) => {
  /* Checking for missing required props */
  if (!variant)
    throw new Error(
      `You need to pass a prop called "variant" of type string to the Avatar component. To learn more about the list of required props for the
      Avatar component go to Maverick documentation at: https://thryvlabs-maverick.netlify.app/?path=/docs/miscellaneous-avatars`
    );
  /* Checking for missing required props */
  const getSizeStyles = (size: string | undefined) => {
    const smallSize = 'w-[30px] h-[30px]';
    const defaultSize = 'w-[45px] h-[45px]';
    const largeSize = 'w-[80px] h-[80px]';
    const extraLargeSize = 'w-[100px] h-[100px]';

    return size === 'small'
      ? smallSize
      : size === 'default'
      ? defaultSize
      : size === 'large'
      ? largeSize
      : size === 'extra-large'
      ? extraLargeSize
      : defaultSize;
  };

  const renderNameAvatar = variant === 'name' && name && (
    <NameAvatar
      className={className || ''}
      sizeStyles={getSizeStyles(size)}
      size={size}
      name={name}
      staticBackground={staticBackground}
      backgroundColor={backgroundColor}
      {...rest}
    />
  );
  const renderUnidentifiedAvatar = variant === 'unidentified' && (
    <UnidentifiedAvatar
      className={className || ''}
      sizeStyles={getSizeStyles(size)}
      {...rest}
    />
  );
  const renderImageAvatar = variant === 'image' && imageUrl && (
    <ImageAvatar
      className={className || ''}
      imageUrl={imageUrl}
      size={size}
      sizeStyles={getSizeStyles(size)}
      {...rest}
    />
  );
  const renderIconAvatar = variant === 'icon' && (
    <IconAvatar
      className={className || ''}
      sizeStyles={getSizeStyles(size)}
      backgroundColor={backgroundColor}
      {...rest}
    >
      {children}
    </IconAvatar>
  );
  return (
    <>
      {renderNameAvatar}
      {renderUnidentifiedAvatar}
      {renderImageAvatar}
      {renderIconAvatar}
    </>
  );
};
